"use client";

import { useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { content } from "@/config/content";
import { useCart } from "@/components/shared/CartProvider";
import ImagePlaceholder from "@/components/shared/ImagePlaceholder";
import { useSiteSettings } from "@/lib/useSiteSettings";
import { useSiteContent } from "@/lib/useSiteContent";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
import { useFocusTrap } from "@/lib/useFocusTrap";
import { MEMBERS_ENABLED } from "@/lib/featureFlags";
import { trackBeginCheckout } from "@/lib/analytics";
import RegionSelector, {
  CheckoutNotice,
} from "@/components/public/RegionSelector";
import FpsPaymentPanel from "@/components/public/FpsPaymentPanel";
import LineAddFriendButton from "@/components/public/LineAddFriendButton";
import InstagramDmButton from "@/components/public/InstagramDmButton";
import { useCheckoutOpen } from "@/lib/checkoutChrome";
import {
  DEFAULT_COUNTRY_CODE,
  REGION,
  buildOverseasConfig,
  isRegionCheckoutBlocked,
  twdToHkd,
  validateOverseasOrder,
} from "@/lib/overseasCheckout";

// 結帳頁最下面那行付款方式說明
const PAYMENT_FOOTNOTE = {
  TW: "結帳將透過綠界 ECPay 安全付款",
  HK: "香港訂單以轉數快 FPS 轉帳付款,送出後會顯示收款資訊",
  CN: "中國訂單透過綠界微信支付付款",
};

function CartItem({ item, onRemove, fileDownload, claimNotice }) {
  const displayPrice =
    Number(item.price) > 0 ? Number(item.price) : fileDownload;
  return (
    <div
      className="flex gap-3 p-3"
      style={{
        backgroundColor: "var(--color-cardBg)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--color-cardBorder)",
      }}
    >
      <div
        className="w-16 h-20 shrink-0 overflow-hidden"
        style={{ borderRadius: "var(--radius-sm, 6px)" }}
      >
        <ImagePlaceholder
          name={item.name}
          image={item.image}
          artworkId={item.id}
          aspectRatio="4/5"
          className="w-full h-full"
        />
      </div>
      <div className="flex-1 min-w-0">
        {/* a11y fix (axe: heading-order): changed h4 → h3 to avoid skipping levels
            after the cart drawer's h2 title. */}
        <h3
          className="text-sm font-medium truncate mb-0.5"
          style={{
            fontFamily: "var(--font-heading)",
            color: "var(--color-text)",
          }}
        >
          {item.name}
        </h3>
        <p className="text-xs mb-2" style={{ color: "var(--color-textMuted)" }}>
          {claimNotice}
        </p>
        <p
          className="text-sm font-bold"
          style={{ color: "var(--color-accent)" }}
        >
          {content.gallery.pricePrefix}
          {displayPrice.toLocaleString()}
        </p>
      </div>
      <button
        onClick={() => onRemove(item.id)}
        className="self-start w-11 h-11 flex items-center justify-center shrink-0 transition-opacity hover:opacity-70"
        style={{ color: "var(--color-textMuted)" }}
        aria-label="移除"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      </button>
    </div>
  );
}

export default function CartDrawer() {
  const { isDrawerOpen } = useCart();
  const pathname = usePathname();
  if (pathname?.startsWith("/admin")) return null;
  if (!isDrawerOpen) return null;
  return <CartDrawerInner />;
}

const FALLBACK_CART_NOTICE = [
  "付款後將直接寄送「圖檔 + 購買憑證」至您的 Email 信箱",
  "若需 PDF 檔案，請加入官方 LINE 附上購買憑證索取",
  "若需客製線上圖檔，請加入官方 LINE 另外討論",
];

function CartDrawerInner() {
  const { items, total, count, removeItem, clearCart, closeDrawer } = useCart();
  const { settings } = useSiteSettings("pricing");
  // Phase 3.4 — cart UI copy from CMS (category='cart')
  const { settings: cart } = useSiteSettings("cart");
  const { items: cartNoticeRows } = useSiteContent("cart_notice");
  const cartNotices =
    cartNoticeRows.length > 0
      ? cartNoticeRows.map((r) => r.body || r.title).filter(Boolean)
      : FALLBACK_CART_NOTICE;
  const fileDownload = Number(settings.price_file_download || 2500);
  const resolvedTotal = items.reduce(
    (sum, it) => sum + (Number(it.price) > 0 ? Number(it.price) : fileDownload),
    0,
  );
  const backdropRef = useRef(null);
  const panelRef = useRef(null);
  const formContainerRef = useRef(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  // 2026-10 海外購買(香港 FPS / 中國微信支付);設定在後台「海外購買」
  const { settings: overseasSettings } = useSiteSettings("overseas");
  // brand 設定帶進來是為了 brand_line_id(「加官方 LINE」按鈕的預設連結)
  const { settings: brandSettings } = useSiteSettings("brand");
  const overseas = useMemo(
    () => buildOverseasConfig({ ...brandSettings, ...overseasSettings }),
    [brandSettings, overseasSettings],
  );
  const [region, setRegion] = useState(REGION.TW);
  const [phoneCountryCode, setPhoneCountryCode] = useState(
    DEFAULT_COUNTRY_CODE.HK,
  );
  const [fpsResult, setFpsResult] = useState(null);
  const isOverseas = region !== REGION.TW;
  const regionBlocked = isRegionCheckoutBlocked(region, overseas);
  const handleRegionChange = (next) => {
    setRegion(next);
    setCheckoutError(null);
    if (DEFAULT_COUNTRY_CODE[next])
      setPhoneCountryCode(DEFAULT_COUNTRY_CODE[next]);
  };

  useBodyScrollLock(true);
  // 購物車開著 = 結帳畫面;藏右下 IG 浮動鈕與 Cookie 框(lib/checkoutChrome.js)
  useCheckoutOpen(true);
  useFocusTrap({
    isActive: true,
    containerRef: panelRef,
    onEscape: closeDrawer,
  });

  const handleCheckout = async (e) => {
    e.preventDefault();
    setCheckoutError(null);
    if (isOverseas) {
      // 跟伺服器同一支純函式先驗一次,錯誤直接顯示在表單下方
      const checked = validateOverseasOrder({
        region,
        product: "artwork",
        buyerName,
        buyerEmail,
        phoneCountryCode,
        buyerPhone,
      });
      if (!checked.ok) {
        setCheckoutError(checked.errors[0]);
        return;
      }
      if (regionBlocked) {
        setCheckoutError(overseas.wechatPendingNotice);
        return;
      }
    }
    setIsSubmitting(true);

    try {
      // Idempotency-Key 讓後端對網路 retry / React double-fire 去重。
      // 搭配 orders.idempotency_key partial UNIQUE index(2026-04-21 migration)。
      const idempotencyKey = crypto.randomUUID();
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            id: item.id,
            name: item.name,
            price: item.price,
          })),
          buyerName,
          buyerEmail,
          buyerPhone,
          ...(isOverseas ? { region, phoneCountryCode } : {}),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setCheckoutError(
          data.error || cart.cart_checkout_error || "結帳失敗，請稍後再試",
        );
        setIsSubmitting(false);
        return;
      }

      // 香港 FPS:不跳綠界,直接在購物車裡顯示轉帳資訊
      if (data.fps) {
        setFpsResult({ ...data.fps, statusUrl: data.statusUrl || null });
        clearCart();
        setIsSubmitting(false);
        return;
      }

      // Inject ECPay form HTML and auto-submit
      if (data.formHTML) {
        const container = formContainerRef.current;
        if (container) {
          container.innerHTML = data.formHTML;
          const form = container.querySelector("form");
          if (form) form.submit();
        }
      }
    } catch (err) {
      setCheckoutError(cart.cart_network_error || "網路錯誤，請稍後再試");
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    backgroundColor: "var(--color-cardBg)",
    border: "1px solid var(--color-cardBorder)",
    borderRadius: "var(--radius-md)",
    color: "var(--color-text)",
    outline: "none",
  };

  return (
    <div
      ref={backdropRef}
      role="dialog"
      aria-modal="true"
      aria-label="購物車"
      className="fixed inset-0 z-[80]"
      onClick={(e) => {
        if (e.target === backdropRef.current) closeDrawer();
      }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 animate-fade-in"
        style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
      />

      {/* Drawer — full-screen bottom sheet on mobile, right panel on desktop */}
      <div
        ref={panelRef}
        className="absolute right-0 top-0 bottom-0 w-full md:w-[400px] flex flex-col animate-slide-in-right"
        style={{
          backgroundColor: "var(--color-bg)",
          boxShadow: "-4px 0 40px rgba(0,0,0,0.1)",
          overscrollBehavior: "contain",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 shrink-0"
          style={{ borderBottom: "1px solid var(--color-cardBorder)" }}
        >
          <div className="flex items-center gap-2">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--color-text)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            <h2
              className="text-base font-bold"
              style={{
                fontFamily: "var(--font-heading)",
                color: "var(--color-text)",
              }}
            >
              {cart.cart_title || "購物車"}
            </h2>
            {count > 0 && (
              <span
                className="text-xs font-medium px-2 py-0.5"
                style={{
                  backgroundColor: "var(--color-accent)",
                  color: "#fff",
                  borderRadius: "var(--radius-full)",
                }}
              >
                {count}
              </span>
            )}
          </div>
          <button
            onClick={closeDrawer}
            className="w-11 h-11 flex items-center justify-center"
            aria-label="關閉購物車"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--color-text)"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>

        {/* Cart items */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4">
            {fpsResult ? (
              <div className="flex flex-col gap-4 py-2">
                <div className="text-center">
                  <p
                    className="text-lg font-bold"
                    style={{
                      fontFamily: "var(--font-heading)",
                      color: "var(--color-text)",
                    }}
                  >
                    訂單已成立
                  </p>
                  <p
                    className="text-xs mt-1"
                    style={{ color: "var(--color-textMuted)" }}
                  >
                    付款資訊也已寄到你的 Email,確認收款後會寄出圖檔
                  </p>
                </div>
                <FpsPaymentPanel
                  fps={fpsResult}
                  lineUrl={overseas.lineUrl}
                  igUrl={overseas.igUrl}
                />
                {fpsResult.statusUrl && (
                  <a
                    href={fpsResult.statusUrl}
                    className="w-full py-2.5 text-sm font-medium text-center"
                    style={{
                      color: "var(--color-accent)",
                      border: "1px solid var(--color-accent)",
                      borderRadius: "var(--radius-full)",
                    }}
                  >
                    查看訂單狀態
                  </a>
                )}
                <button
                  onClick={closeDrawer}
                  className="w-full py-2.5 text-sm font-medium text-white"
                  style={{
                    backgroundColor: "var(--color-accent)",
                    borderRadius: "var(--radius-full)",
                  }}
                >
                  完成
                </button>
              </div>
            ) : count === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 py-12">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--color-textMuted)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.4"
                >
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                <p
                  className="text-sm"
                  style={{ color: "var(--color-textMuted)" }}
                >
                  {cart.cart_empty_state || "還沒有選擇作品"}
                </p>
                <button
                  onClick={closeDrawer}
                  className="text-sm font-medium px-4 py-2 mt-1 transition-opacity hover:opacity-80"
                  style={{
                    color: "var(--color-accent)",
                    border: "1px solid var(--color-accent)",
                    borderRadius: "var(--radius-full)",
                  }}
                >
                  去逛逛
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {items.map((item) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    onRemove={removeItem}
                    fileDownload={fileDownload}
                    claimNotice={cart.cart_claim_notice || "認領圖 · 僅此一張"}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer — checkout */}
          {count > 0 && (
            <div
              className="p-5"
              style={{
                borderTop: "1px solid var(--color-cardBorder)",
                paddingBottom: "calc(20px + var(--safe-area-bottom))",
              }}
            >
              {/* Subtotal */}
              <div className="flex items-center justify-between mb-4">
                <span
                  className="text-sm"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  {cart.cart_subtotal_label || "小計"}（{count} 件作品）
                </span>
                <span
                  className="text-lg font-bold"
                  style={{ color: "var(--color-text)" }}
                >
                  {content.gallery.pricePrefix}
                  {resolvedTotal.toLocaleString()}
                </span>
              </div>

              {/* Phase 3: member tier discount hint (hidden when member system disabled) */}
              {MEMBERS_ENABLED && (
                <p
                  className="text-xs mb-3"
                  style={{ color: "var(--color-textMuted)", lineHeight: 1.5 }}
                >
                  會員等級折扣將於結帳時自動套用（登入後）
                </p>
              )}

              {/* Purchase info notice */}
              <div
                style={{
                  backgroundColor: "var(--color-cardBg)",
                  border: "1px solid var(--color-cardBorder)",
                  borderRadius: "var(--radius-md)",
                  padding: "12px",
                  marginBottom: "12px",
                }}
              >
                <p
                  style={{
                    fontSize: "12px",
                    color: "var(--color-textMuted)",
                    lineHeight: 1.6,
                    marginBottom: "4px",
                  }}
                >
                  · 所有線上圖檔均為 NT$
                  {Number(
                    settings.price_file_download || "2500",
                  ).toLocaleString()}
                </p>
                <p
                  style={{
                    fontSize: "12px",
                    color: "var(--color-accent)",
                    lineHeight: 1.6,
                    marginBottom: "4px",
                    fontWeight: 500,
                  }}
                >
                  · 此為線上圖檔授權費用，不含實體刺青操作
                </p>
                {cartNotices.map((note, i) => (
                  <p
                    key={i}
                    style={{
                      fontSize: "12px",
                      color: "var(--color-textMuted)",
                      lineHeight: 1.6,
                      marginBottom: i === cartNotices.length - 1 ? "0" : "4px",
                    }}
                  >
                    · {note}
                  </p>
                ))}
              </div>

              {!showCheckoutForm ? (
                /* Checkout button — expand form */
                <button
                  onClick={() => {
                    // GA4/Meta begin_checkout — resolved prices, fallback applied
                    trackBeginCheckout(
                      items.map((it) => ({
                        id: it.id,
                        name: it.name,
                        price:
                          Number(it.price) > 0
                            ? Number(it.price)
                            : fileDownload,
                      })),
                      resolvedTotal,
                    );
                    setShowCheckoutForm(true);
                  }}
                  className="w-full py-3.5 text-sm font-medium text-white text-center transition-transform active:scale-[0.98]"
                  style={{
                    backgroundColor: "var(--color-accent)",
                    borderRadius: "var(--radius-full)",
                  }}
                >
                  結帳領取認領圖
                </button>
              ) : (
                /* Checkout form */
                <form onSubmit={handleCheckout} className="flex flex-col gap-3">
                  <div>
                    <p
                      className="text-xs mb-1.5 font-medium"
                      style={{ color: "var(--color-textSecondary)" }}
                    >
                      購買地區
                    </p>
                    <RegionSelector
                      value={region}
                      onChange={handleRegionChange}
                    />
                    {/* 選中國當下就提示(不等填完整張表才看到):未開通附 LINE / IG 按鈕+實名說明 */}
                    {region === REGION.CN && (
                      <div className="flex flex-col gap-2" style={{ marginTop: "10px" }}>
                        {regionBlocked && (
                          <CheckoutNotice
                            tone="warn"
                            text={overseas.wechatPendingNotice}
                          >
                            <LineAddFriendButton href={overseas.lineUrl} />
                            <InstagramDmButton href={overseas.igUrl} />
                          </CheckoutNotice>
                        )}
                        <CheckoutNotice
                          tone="note"
                          text={overseas.wechatRealnameNotice}
                        />
                      </div>
                    )}
                  </div>
                  <label htmlFor="cart-buyer-name" className="sr-only">
                    {cart.cart_name_placeholder || "怎麼稱呼你"}
                  </label>
                  <input
                    id="cart-buyer-name"
                    name="buyer_name"
                    type="text"
                    autoComplete="name"
                    placeholder={cart.cart_name_placeholder || "怎麼稱呼你"}
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                    required
                    className="w-full px-3 py-2.5 text-sm"
                    style={inputStyle}
                  />
                  <label htmlFor="cart-buyer-email" className="sr-only">
                    {cart.cart_email_placeholder || "Email（圖檔會寄到這裡）"}
                  </label>
                  <input
                    id="cart-buyer-email"
                    name="buyer_email"
                    type="email"
                    autoComplete="email"
                    placeholder={
                      cart.cart_email_placeholder || "Email（圖檔會寄到這裡）"
                    }
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                    required
                    className="w-full px-3 py-2.5 text-sm"
                    style={inputStyle}
                  />
                  {isOverseas ? (
                    <div className="flex gap-2">
                      <label htmlFor="cart-buyer-cc" className="sr-only">
                        國碼
                      </label>
                      <input
                        id="cart-buyer-cc"
                        name="phone_country_code"
                        type="text"
                        inputMode="tel"
                        value={phoneCountryCode}
                        onChange={(e) => setPhoneCountryCode(e.target.value)}
                        required
                        className="px-3 py-2.5 text-sm"
                        style={{ ...inputStyle, width: "80px", flex: "none" }}
                      />
                      <label htmlFor="cart-buyer-phone" className="sr-only">
                        電話號碼
                      </label>
                      <input
                        id="cart-buyer-phone"
                        name="buyer_phone"
                        type="tel"
                        autoComplete="tel-national"
                        placeholder={
                          region === REGION.HK
                            ? "電話:8 位數字,例:9123 4567"
                            : "電話:11 位數字,例:138 1234 5678"
                        }
                        value={buyerPhone}
                        onChange={(e) => setBuyerPhone(e.target.value)}
                        required
                        className="px-3 py-2.5 text-sm"
                        style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                      />
                    </div>
                  ) : (
                    <>
                      <label htmlFor="cart-buyer-phone" className="sr-only">
                        {cart.cart_phone_placeholder || "手機（方便聯絡你）"}
                      </label>
                      <input
                        id="cart-buyer-phone"
                        name="buyer_phone"
                        type="tel"
                        autoComplete="tel"
                        placeholder={
                          cart.cart_phone_placeholder || "手機（方便聯絡你）"
                        }
                        value={buyerPhone}
                        onChange={(e) => setBuyerPhone(e.target.value)}
                        required
                        pattern="09\d{8}"
                        title={
                          cart.cart_phone_validation_error ||
                          "請輸入 09 開頭的 10 碼手機號碼"
                        }
                        className="w-full px-3 py-2.5 text-sm"
                        style={inputStyle}
                      />
                    </>
                  )}
                  {region === REGION.HK && (
                    <p
                      className="text-xs"
                      style={{ color: "var(--color-accent)" }}
                    >
                      轉數快應付約 HK$
                      {twdToHkd(resolvedTotal, overseas.hkdRate)}
                      (以送出後顯示的金額為準)
                    </p>
                  )}
                  <CheckoutNotice text={overseas.cartLeadTimeNotice} />
                  <button
                    type="submit"
                    disabled={isSubmitting || regionBlocked}
                    className="w-full py-3.5 text-sm font-medium text-white text-center transition-transform active:scale-[0.98] disabled:opacity-50"
                    style={{
                      backgroundColor: "var(--color-accent)",
                      borderRadius: "var(--radius-full)",
                    }}
                  >
                    {isSubmitting
                      ? cart.cart_processing_button || "處理中..."
                      : region === REGION.HK
                        ? "送出訂單(轉數快 FPS 付款)"
                        : regionBlocked
                          ? "微信支付開通中"
                          : cart.cart_checkout_button || "確認付款"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCheckoutForm(false)}
                    className="w-full py-2 text-xs text-center transition-opacity hover:opacity-70"
                    style={{ color: "var(--color-textMuted)" }}
                  >
                    {cart.cart_back_button || "返回"}
                  </button>
                </form>
              )}

              {checkoutError && !fpsResult && (
                <p
                  className="text-center text-xs mt-2"
                  style={{ color: "#e53e3e" }}
                >
                  {checkoutError}
                </p>
              )}

              <p
                className="text-center text-xs mt-3"
                style={{ color: "var(--color-textMuted)" }}
              >
                {PAYMENT_FOOTNOTE[region]}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Hidden container for ECPay form auto-submit */}
      <div ref={formContainerRef} style={{ display: "none" }} />
    </div>
  );
}
